import {
  ApplicationConfig,
  ErrorHandler,
  Inject,
  Optional,
  Provider,
  importProvidersFrom,
  provideZonelessChangeDetection
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { provideHttpClient, withFetch, withInterceptorsFromDi, HTTP_INTERCEPTORS } from '@angular/common/http';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { routes } from './app.routes';
import { AuthInterceptor } from './core/auth-interceptor.service';
import { ErrorInterceptor } from './core/error-interceptor.service';
import { GlobalErrorHandler } from './core/global-error-handler.service';
import { SSR_BACKEND_BASE_URL } from './core/tokens/ssr-backend-base-url.token';
import { ApiModule, OpenAPI } from './modules/api';
import { Environment } from './core/environment';

const isBrowser = typeof window !== 'undefined';

/**
 * Resolves the API base URL.
 * - Browser: use the page origin (same-origin /api proxy), except for the
 *   Angular CLI dev server on :4200, where the local backend is used.
 * - SSR: prefer the backend URL injected by server.ts. This is required for
 *   production blue-green deployments where the backend container hostname
 *   is not the hard-coded 'backend' value from Environment.localApiURL.
 */
const getBaseUrl = (ssrBackendBaseUrl?: string): string => {
  if (isBrowser) {
    const origin = window.location.origin;
    if (origin.includes('localhost:4200') || origin.includes('127.0.0.1:4200')) {
      return Environment.localApiURL;
    }
    return origin;
  }
  return ssrBackendBaseUrl || Environment.localApiURL;
};

const openAPIProvider: Provider = {
  provide: OpenAPI,
  useFactory: (ssrBackendBaseUrl?: string) => ({
    BASE: getBaseUrl(ssrBackendBaseUrl),
    VERSION: '4.0',
    WITH_CREDENTIALS: true,
    CREDENTIALS: 'include',
    TOKEN: () => {
      if (isBrowser) {
        return localStorage.getItem('access_token') || undefined;
      }
      return undefined;
    },
    USERNAME: undefined,
    PASSWORD: undefined,
    HEADERS: {},
    ENCODE_PATH: undefined,
  }),
  deps: [[new Optional(), new Inject(SSR_BACKEND_BASE_URL)]],
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes),
    provideZonelessChangeDetection(),
    provideClientHydration(),
    provideAnimationsAsync(),
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    provideCharts(withDefaultRegisterables()),
    importProvidersFrom(ApiModule, MatSnackBarModule),
    openAPIProvider,
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: ErrorHandler, useClass: GlobalErrorHandler }
  ]
};
